import { useEffect } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { Building2, LayoutTemplate, Mail, ScrollText, Users } from "lucide-react";
import { SideNav, type SideNavItem } from "../components/SideNav";
import { MADEMIND_SECTIONS, type MademindSection } from "./router-mademind";

const SECTION_LABEL: Record<MademindSection, string> = {
  tenants: "Tenants",
  operators: "Operatori",
  audit: "Audit",
  templates: "Templates",
  emails: "Email",
};

const SECTION_ICON: Record<MademindSection, NonNullable<SideNavItem["icon"]>> = {
  tenants: Building2,
  operators: Users,
  audit: ScrollText,
  templates: LayoutTemplate,
  emails: Mail,
};

/** `/mademind-slug/emails` → `emails`; anything unrecognised → null. */
function sectionFromPath(pathname: string): MademindSection | null {
  const segment = pathname.split("/").filter(Boolean)[1];
  return MADEMIND_SECTIONS.find((s) => s === segment) ?? null;
}

/**
 * Console shell: navigation rail plus the routed section.
 *
 * The rail navigates rather than flipping local state, so the address bar,
 * the back button and a reload all agree on where you are.
 *
 * SideNav consumes `useCore()` internally; this is mounted under
 * MademindBoot's CoreProvider, so no direct core access is needed here.
 */
export function MademindLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const active = sectionFromPath(location.pathname);

  // The browser tab says which console section you are on — it is how you find
  // the right window when several are open.
  useEffect(() => {
    const label = active === null ? null : SECTION_LABEL[active];
    document.title = label === null ? "Praesto Console" : `${label} · Praesto Console`;
  }, [active]);

  const items: SideNavItem[] = MADEMIND_SECTIONS.map((key) => ({
    key,
    label: SECTION_LABEL[key],
    icon: SECTION_ICON[key],
    onClick: () => void navigate({ to: `/${tenantSlug}/${key}` as never }),
    active: active === key,
  }));

  return (
    <section className="flex min-h-[100dvh] flex-col md:flex-row">
      <SideNav title="Praesto Console" items={items} />
      <div className="flex-1 min-w-0 px-6 py-10">
        {/* On desktop the rail header is the title; show this brand header only
            on mobile (rail hidden) so "Praesto Console" isn't duplicated. */}
        <div className="text-eyebrow mb-6 flex items-center gap-2 md:hidden">
          <span>Praesto Console</span>
        </div>
        <Outlet />
      </div>
    </section>
  );
}
